import * as actions from './users.actions';
import { ISearchUser } from '@account-users/user/models/ISearchUser';
import { FormState } from '@shared';
import { IBulkEdit } from '@account-users/account/models/LicensePermissionList';
import { cloneDeep } from 'lodash';

export const USERS_FEATURE_KEY = 'users';

export interface UserState {
  users: ISearchUser[];
  isLoadingUsers: boolean;
  bulkEditIsLoading: boolean;
  bulkEditFormState: FormState;
  bulkEdit: IBulkEdit[];
  bulkEditSuccess: IBulkEdit[];
  bulkEditFailed: IBulkEdit[];
  selectedUsers: ISearchUser[];
  exportAllAccountUsers: string;
}

export const initialState: UserState = {
  users: [],
  isLoadingUsers: false,
  bulkEditIsLoading: false,
  bulkEditFormState: FormState.confirm,
  bulkEdit: [],
  bulkEditSuccess: [],
  bulkEditFailed: [],
  selectedUsers: [],
  exportAllAccountUsers: null,
};

export function reducer(
  state: UserState = initialState,
  action: actions.UserActions,
): UserState {
  switch (action.type) {
    case actions.UserActionTypes.LOAD_USERS_SUCCESS: {
      return {
        ...state,
        users: action.payload,
        isLoadingUsers: false,
      };
    }

    case actions.UserActionTypes.LOAD_USERS: {
      return {
        ...state,
        users: [],
        isLoadingUsers: true,
      };
    }

    case actions.UserActionTypes.LOAD_ACCOUNT_USERS: {
      return {
        ...state,
        users: [],
        isLoadingUsers: true,
      };
    }

    case actions.UserActionTypes.RESET_ACCOUNT_USERS: {
      return {
        ...state,
        isLoadingUsers: false,
        users: [],
        selectedUsers: [],
      };
    }

    case actions.UserActionTypes.LOAD_USERS_FAILED: {
      return {
        ...state,
        isLoadingUsers: false,
      };
    }

    case actions.UserActionTypes.SET_BULK_EDIT: {
      const { users, licenses } = action.payload;
      const updateUserObj = [];
      users.forEach((user) => {
        licenses.forEach((license) => {
          license.permissions.forEach((permission) => {
            updateUserObj.push({
              accountId: user.accountId,
              email: user.email,
              userId: user.userId,
              licenseCode: license.code,
              permissionCode: permission,
            });
          });
        });
      });
      return {
        ...state,
        bulkEdit: updateUserObj,
      };
    }

    case actions.UserActionTypes.BULK_EDIT_ACCOUNT_USER_PERMISSIONS: {
      return {
        ...state,
        bulkEditFormState: FormState.submitting,
      };
    }

    case actions.UserActionTypes.BULK_EDIT_ACCOUNT_USER_PERMISSIONS_SUCCESS: {
      const { success, failed } = action.payload;
      const successWithEmail = success.map((successUser) => {
        const user = [...state.users].find(
          (usr) => usr.userId === successUser.userId,
        );
        return {
          accountId: successUser.accountId,
          email: user.email,
          userId: successUser.userId,
          licenseCode: successUser.licenseCode,
          permissionCode: successUser.permissionCode,
        };
      });

      const failWithEmail = failed.map((successUser) => {
        const user = [...state.users].find(
          (usr) => usr.userId === successUser.userId,
        );
        return {
          accountId: successUser.accountId,
          email: user.email,
          userId: successUser.userId,
          licenseCode: successUser.licenseCode,
          permissionCode: successUser.permissionCode,
        };
      });
      return {
        ...state,
        bulkEditFormState: FormState.success,
        bulkEditSuccess: successWithEmail,
        bulkEditFailed: failWithEmail,
      };
    }

    case actions.UserActionTypes.BULK_EDIT_ACCOUNT_USER_PERMISSIONS_FAILURE: {
      return {
        ...state,
        bulkEditFormState: FormState.failure,
      };
    }

    case actions.UserActionTypes.RESET_BULK_EDIT: {
      return {
        ...state,
        bulkEditFormState: FormState.confirm,
        bulkEdit: [],
        bulkEditSuccess: [],
        bulkEditFailed: [],
      };
    }

    case actions.UserActionTypes.SELECT_USER: {
      const { user } = { ...action.payload };
      const alreadySelected = [...state.selectedUsers].some(
        (u) => u.userId === user.userId && u.accountId === user.accountId,
      );
      const selectedUsers = alreadySelected
        ? state.selectedUsers.filter(
            (u) => u.userId !== user.userId || u.accountId !== user.accountId,
          )
        : [...state.selectedUsers, user];

      return {
        ...state,
        selectedUsers: selectedUsers,
      };
    }

    case actions.UserActionTypes.SELECT_ALL_USERS: {
      const { users } = action.payload;
      return {
        ...state,
        selectedUsers: users,
      };
    }

    case actions.UserActionTypes.RESET_SELECTED_USERS: {
      return {
        ...state,
        selectedUsers: [],
      };
    }

    case actions.UserActionTypes.DOWNLOAD_ACCOUNT_USER_CSV: {
      const exportAllAccountUsers = action.payload;
      const users = cloneDeep(state.users);
      const csvUsers = exportAllAccountUsers
        ? users
        : users.filter(
            (user, i, self) =>
              i === self.findIndex((u) => u.email === user.email),
          );

      if (!exportAllAccountUsers) {
        csvUsers.forEach((user) => {
          let accountIds = '';
          users.forEach((u) => {
            if (u.email === user.email) {
              accountIds += u.accountId + ' ';
            }
          });
          user.accountId = accountIds;
        });
      }

      const columnNames = exportAllAccountUsers
        ? ['Email', 'Name', 'Account (ID)']
        : ['Email', 'Name', 'Account (IDs)'];
      const header = columnNames.join(',');

      let csv = header;
      csv += '\r\n';

      csvUsers.map((c) => {
        csv += (exportAllAccountUsers
          ? [c.email, c.name, c.accountId]
          : [c.email, c.name, c.accountId]
        ).join(',');
        csv += '\r\n';
      });
      return {
        ...state,
        exportAllAccountUsers: csvUsers.length ? csv : '',
      };
    }

    default:
      return state;
  }
}
